import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { 
  ArrowLeft, 
  Calendar,
  Clock,
  MapPin,
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react';

const TrainingForm = () => {
  const { id } = useParams(); // Si existe ID, es edición
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    team: userProfile?.role === 'entrenador' ? userProfile.team : 'Juvenil',
    date: new Date().toISOString().split('T')[0],
    start_time: '18:00',
    end_time: '20:00',
    location: 'Campo Municipal',
    type: 'Entrenamiento',
    training_type: '',
    objectives: '',
    weather: '',
    materials: '',
    incidents: '',
    description: '',
    notes: ''
  });

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      fetchTraining();
    }
  }, [id]);

  const fetchTraining = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('trainings')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          team: data.team,
          date: data.date,
          start_time: data.start_time,
          end_time: data.end_time,
          location: data.location || '',
          type: data.type || 'Entrenamiento',
          training_type: data.training_type || '',
          objectives: data.objectives || '',
          weather: data.weather || '',
          materials: data.materials || '',
          incidents: data.incidents || '',
          description: data.description || '',
          notes: data.notes || ''
        });
      }
    } catch (err) {
      console.error('Error al cargar entrenamiento:', err);
      setError('Error al cargar el entrenamiento');
    } finally {
      setLoading(false);
    }
  };

  const teams = ['Juvenil', 'Cadete', 'Amateur'];
  const types = ['Entrenamiento', 'Partido amistoso', 'Partido oficial'];
  const trainingTypes = [
    { value: '', label: 'No especificado' },
    { value: 'fisico', label: 'Físico' },
    { value: 'tecnico', label: 'Técnico' },
    { value: 'tactico', label: 'Táctico' },
    { value: 'mixto', label: 'Mixto' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validaciones
      if (!formData.date) {
        throw new Error('La fecha es obligatoria');
      }

      if (!formData.start_time || !formData.end_time) {
        throw new Error('La hora de inicio y fin son obligatorias');
      }

      // Verificar que la hora de fin sea posterior a la de inicio
      if (formData.start_time >= formData.end_time) {
        throw new Error('La hora de fin debe ser posterior a la hora de inicio');
      }

      const trainingData = {
        team: formData.team,
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        location: formData.location || null,
        type: formData.type,
        training_type: formData.training_type || null,
        objectives: formData.objectives || null,
        weather: formData.weather || null,
        materials: formData.materials || null,
        incidents: formData.incidents || null,
        description: formData.description || null,
        notes: formData.notes || null,
        created_by: userProfile.id
      };

      if (isEditMode) {
        // Actualizar entrenamiento existente
        const { error: updateError } = await supabase
          .from('trainings')
          .update(trainingData)
          .eq('id', id);

        if (updateError) throw updateError;
      } else {
        // Crear nuevo entrenamiento
        const { error: insertError } = await supabase
          .from('trainings')
          .insert([trainingData]);

        if (insertError) throw insertError;
      }

      setSuccess(true);

      setTimeout(() => {
        navigate('/trainings');
      }, 2000);

    } catch (err) {
      console.error('Error al crear entrenamiento:', err);
      setError(err.message || 'Error al crear el entrenamiento');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {isEditMode ? '¡Entrenamiento actualizado!' : '¡Entrenamiento creado!'}
          </h2>
          <p className="text-gray-600 mb-4">
            {isEditMode ? 'El entrenamiento se ha actualizado correctamente' : 'El entrenamiento se ha registrado correctamente'}
          </p>
          <button
            onClick={() => navigate('/trainings')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver a Entrenamientos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/trainings')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditMode ? 'Editar Entrenamiento' : 'Nuevo Entrenamiento'}
              </h1>
              <p className="text-sm text-gray-600">
                {isEditMode ? 'Modifica los datos del entrenamiento' : 'Registra un nuevo entrenamiento o partido'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-8">
          <div className="space-y-6">
            {/* Equipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Equipo <span className="text-red-500">*</span>
              </label>
              <select
                name="team"
                value={formData.team}
                onChange={handleInputChange}
                required
                disabled={userProfile?.role === 'entrenador'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                {teams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>

            {/* Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo <span className="text-red-500">*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {types.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Fecha */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Fecha <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Ubicación */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Ubicación
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Ej: Campo Municipal"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Hora inicio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Hora de inicio <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Hora fin */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Hora de fin <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Tipo de entrenamiento */}
            {formData.type === 'Entrenamiento' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de entrenamiento
                </label>
                <select
                  name="training_type"
                  value={formData.training_type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {trainingTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Objetivos */}
            {formData.type === 'Entrenamiento' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Objetivos del entrenamiento
                </label>
                <textarea
                  name="objectives"
                  value={formData.objectives}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Ej: Mejorar presión alta, trabajar físico anaeróbico..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Clima */}
              {formData.type === 'Entrenamiento' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clima / Condiciones
                  </label>
                  <input
                    type="text"
                    name="weather"
                    value={formData.weather}
                    onChange={handleInputChange}
                    placeholder="Ej: Soleado, 25°C"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* Material */}
              {formData.type === 'Entrenamiento' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Material utilizado
                  </label>
                  <input
                    type="text"
                    name="materials"
                    value={formData.materials}
                    onChange={handleInputChange}
                    placeholder="Ej: Conos, petos, balones medicinales..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
            </div>

            {/* Incidencias */}
            {formData.type === 'Entrenamiento' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Incidencias
                </label>
                <textarea
                  name="incidents"
                  value={formData.incidents}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Problemas con el campo, material dañado, etc..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Descripción del entrenamiento o partido..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Notas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notas adicionales
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="2"
                placeholder="Notas internas (opcional)..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Botones */}
          <div className="flex items-center justify-end gap-4 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/trainings')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader className="w-4 h-4 animate-spin" />}
              {loading ? (isEditMode ? 'Actualizando...' : 'Creando...') : (isEditMode ? 'Actualizar Entrenamiento' : 'Crear Entrenamiento')}
            </button>
          </div>
        </form>

        {/* Información adicional */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ Información</h3>
          <p className="text-sm text-blue-800">
            Después de crear el entrenamiento, podrás pasar lista y marcar la asistencia de los jugadores.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrainingForm;